import {
  cloneElement,
  forwardRef,
  isValidElement,
  useId,
  type InputHTMLAttributes,
  type ReactElement,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";

// forwardRef sur les trois primitives : une page qui déplace une carte doit
// pouvoir rendre le focus au contrôle qui a servi au déplacement.
export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...rest }, ref) {
    return <input ref={ref} className={["input", className].filter(Boolean).join(" ")} {...rest} />;
  },
);

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(function Textarea({ className, ...rest }, ref) {
  return (
    <textarea ref={ref} className={["textarea", className].filter(Boolean).join(" ")} {...rest} />
  );
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, ...rest }, ref) {
    return (
      <select ref={ref} className={["select", className].filter(Boolean).join(" ")} {...rest} />
    );
  },
);

interface FieldProps {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}

/** Props que `Field` injecte dans le contrôle pour le rattacher à son libellé. */
export interface FieldControlProps {
  id?: string;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
}

const NATIVE_CONTROLS = new Set(["input", "select", "textarea"]);

/**
 * Le contrôle reçoit-il les props de rattachement ? Un élément natif de saisie,
 * oui ; un composant (fonction ou forwardRef), oui — il relaie ses props. Un
 * `<div>` de mise en page ou un fragment, non : l'identifiant y serait perdu et
 * le `htmlFor` pointerait sur un élément qu'aucun libellé ne peut nommer.
 */
function acceptsControlProps(node: ReactNode): node is ReactElement<FieldControlProps> {
  if (!isValidElement(node)) return false;
  if (typeof node.type === "string") return NATIVE_CONTROLS.has(node.type);
  return typeof node.type === "function" || typeof node.type === "object";
}

/**
 * Un libellé qui n'est pas rattaché à son champ n'existe pas : il n'est ni
 * annoncé par un lecteur d'écran, ni cliquable pour donner le focus. `Field`
 * génère donc l'identifiant lui-même et le pose sur le contrôle rendu par
 * l'appelant, plutôt que d'attendre que chacun des quelque cinquante appels y
 * pense. Au passage, l'indice et l'erreur sont rattachés par aria-describedby
 * et l'erreur pose aria-invalid — sans quoi le style d'erreur du champ
 * (`[aria-invalid="true"]`) ne se déclenchait jamais.
 */
export function Field({ label, hint, error, children }: FieldProps) {
  const base = useId();
  const hintId = `${base}-hint`;
  const errorId = `${base}-error`;
  const showHint = Boolean(hint) && !error;
  const describedBy =
    [showHint ? hintId : null, error ? errorId : null].filter(Boolean).join(" ") || undefined;

  const attachable = acceptsControlProps(children);
  const controlId = attachable ? (children.props.id ?? `${base}-control`) : undefined;
  const control = attachable
    ? cloneElement(children, {
        id: controlId,
        "aria-invalid": error ? true : children.props["aria-invalid"],
        "aria-describedby": describedBy ?? children.props["aria-describedby"],
      })
    : children;

  return (
    <div className="field">
      <label className="field__label" htmlFor={controlId}>
        {label}
      </label>
      {control}
      {showHint ? (
        <span className="field__hint" id={hintId}>
          {hint}
        </span>
      ) : null}
      {error ? (
        <span className="field__error" id={errorId} role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}
